import React from 'react';
import {
  Container,
  Typography,
  Box,
  Card,
  CardContent,
  Divider
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import SEO from '../components/SEO';
import SEOErrorBoundary from '../components/SEOErrorBoundary';
import useStructuredData from '../hooks/useStructuredData';

const LegalDisclaimer: React.FC = () => {
  const { t, i18n } = useTranslation();
  const { getLegalPageData } = useStructuredData();

  const pageTitle = t('legalDisclaimer.title');
  const pageDescription = i18n.language === 'en'
    ? 'Important legal information about Alerta Legal services. Understand service limitations, liability disclaimers, and your responsibilities when using our legal publication monitoring service.'
    : 'Información legal importante sobre los servicios de Alerta Legal. Comprende las limitaciones del servicio, exenciones de responsabilidad y tus responsabilidades al usar nuestro servicio de monitoreo de publicaciones legales.';

  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://alertalegal.co';
  const canonicalUrl = `${siteUrl}${t('routes.legalDisclaimer')}`;
  const structuredData = getLegalPageData(pageTitle, pageDescription, t('routes.legalDisclaimer'), 'TermsOfService');

  return (
    <>
      <SEOErrorBoundary fallbackTitle="Legal Disclaimer - AlertaLegal" fallbackDescription="Important legal information about Alerta Legal services and limitations.">
        <SEO 
          title={pageTitle}
          description={pageDescription}
          canonical={canonicalUrl}
          structuredData={structuredData}
          hreflangEnabled={true}
        />
      </SEOErrorBoundary>
      <Container maxWidth="md">
        <Box sx={{ mt: 4, mb: 4 }}>
          <Typography 
            variant="h3" 
            component="h1" 
            gutterBottom 
            align="center"
            tabIndex={-1}
            id="main-heading"
            sx={{ 
              fontWeight: 600,
              color: 'primary.main',
              mb: 6,
              '&:focus': {
                outline: 'none'
              }
            }}
          >
            {t('legalDisclaimer.title')}
          </Typography>

          <Card sx={{ boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)' }}>
            <CardContent sx={{ p: { xs: 3, md: 5 } }}>
              {/* Introduction */}
              <Box sx={{ mb: 4 }}>
                <Typography 
                  variant="h5" 
                  component="h2" 
                  gutterBottom
                  sx={{ 
                    fontWeight: 600,
                    color: 'primary.main',
                    mb: 2 
                  }}
                >
                  {t('legalDisclaimer.sections.introduction.title')}
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  {t('legalDisclaimer.sections.introduction.content')}
                </Typography>
              </Box>

              <Divider sx={{ my: 4 }} />

              {/* Service Scope */}
              <Box sx={{ mb: 4 }}>
                <Typography 
                  variant="h5" 
                  component="h2" 
                  gutterBottom
                  sx={{ 
                    fontWeight: 600,
                    color: 'primary.main',
                    mb: 2 
                  }}
                >
                  {t('legalDisclaimer.sections.serviceScope.title')}
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  {t('legalDisclaimer.sections.serviceScope.content')}
                </Typography>
              </Box>

              <Divider sx={{ my: 4 }} />

              {/* Not Legal Advice */}
              <Box sx={{ mb: 4 }}>
                <Typography 
                  variant="h5" 
                  component="h2" 
                  gutterBottom
                  sx={{ 
                    fontWeight: 600,
                    color: 'primary.main',
                    mb: 2 
                  }}
                >
                  {t('legalDisclaimer.sections.notLegalAdvice.title')}
                </Typography>
                <Typography variant="body1" sx={{ mb: 2, fontWeight: 'bold' }}>
                  {t('legalDisclaimer.sections.notLegalAdvice.content')}
                </Typography>
                <Box component="ul" sx={{ pl: 2, mb: 2 }}>
                  <Typography component="li" variant="body1" sx={{ mb: 1 }}>
                    {t('legalDisclaimer.sections.notLegalAdvice.consultation')}
                  </Typography>
                  <Typography component="li" variant="body1" sx={{ mb: 1 }}>
                    {t('legalDisclaimer.sections.notLegalAdvice.noAttorneyRelationship')}
                  </Typography>
                </Box>
              </Box>

              <Divider sx={{ my: 4 }} />

              {/* Accuracy Limitations */}
              <Box sx={{ mb: 4 }}>
                <Typography 
                  variant="h5" 
                  component="h2" 
                  gutterBottom
                  sx={{ 
                    fontWeight: 600,
                    color: 'primary.main',
                    mb: 2 
                  }}
                >
                  {t('legalDisclaimer.sections.accuracyLimitations.title')}
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  {t('legalDisclaimer.sections.accuracyLimitations.content')}
                </Typography>
                <Box component="ul" sx={{ pl: 2, mb: 2 }}>
                  <Typography component="li" variant="body1" sx={{ mb: 1 }}>
                    {t('legalDisclaimer.sections.accuracyLimitations.completeness')}
                  </Typography>
                  <Typography component="li" variant="body1" sx={{ mb: 1 }}>
                    {t('legalDisclaimer.sections.accuracyLimitations.accuracy')}
                  </Typography>
                  <Typography component="li" variant="body1" sx={{ mb: 1 }}>
                    {t('legalDisclaimer.sections.accuracyLimitations.timeliness')}
                  </Typography>
                  <Typography component="li" variant="body1" sx={{ mb: 1 }}>
                    {t('legalDisclaimer.sections.accuracyLimitations.errorPossibility')}
                  </Typography>
                </Box>
              </Box>

              <Divider sx={{ my: 4 }} />

              {/* User Responsibilities */}
              <Box sx={{ mb: 4 }}>
                <Typography 
                  variant="h5" 
                  component="h2" 
                  gutterBottom
                  sx={{ 
                    fontWeight: 600,
                    color: 'primary.main',
                    mb: 2 
                  }}
                >
                  {t('legalDisclaimer.sections.userResponsibilities.title')}
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  {t('legalDisclaimer.sections.userResponsibilities.content')}
                </Typography>
                <Box component="ul" sx={{ pl: 2, mb: 2 }}>
                  <Typography component="li" variant="body1" sx={{ mb: 1 }}>
                    {t('legalDisclaimer.sections.userResponsibilities.verification')}
                  </Typography>
                  <Typography component="li" variant="body1" sx={{ mb: 1 }}>
                    {t('legalDisclaimer.sections.userResponsibilities.officialSources')}
                  </Typography>
                  <Typography component="li" variant="body1" sx={{ mb: 1 }}>
                    {t('legalDisclaimer.sections.userResponsibilities.professionalAdvice')}
                  </Typography>
                  <Typography component="li" variant="body1" sx={{ mb: 1 }}>
                    {t('legalDisclaimer.sections.userResponsibilities.compliance')}
                  </Typography>
                  <Typography component="li" variant="body1" sx={{ mb: 1 }}>
                    {t('legalDisclaimer.sections.userResponsibilities.accountSecurity')}
                  </Typography>
                </Box>
              </Box>

              <Divider sx={{ my: 4 }} />

              {/* Limitation of Liability */}
              <Box sx={{ mb: 4 }}>
                <Typography 
                  variant="h5" 
                  component="h2" 
                  gutterBottom
                  sx={{ 
                    fontWeight: 600,
                    color: 'primary.main',
                    mb: 2 
                  }}
                >
                  {t('legalDisclaimer.sections.liability.title')}
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  {t('legalDisclaimer.sections.liability.content')}
                </Typography>
                <Box component="ul" sx={{ pl: 2, mb: 2 }}>
                  <Typography component="li" variant="body1" sx={{ mb: 1 }}>
                    {t('legalDisclaimer.sections.liability.directDamages')}
                  </Typography>
                  <Typography component="li" variant="body1" sx={{ mb: 1 }}>
                    {t('legalDisclaimer.sections.liability.lostOpportunities')}
                  </Typography>
                  <Typography component="li" variant="body1" sx={{ mb: 1 }}>
                    {t('legalDisclaimer.sections.liability.serviceInterruptions')}
                  </Typography>
                  <Typography component="li" variant="body1" sx={{ mb: 1 }}>
                    {t('legalDisclaimer.sections.liability.dataLoss')}
                  </Typography>
                  <Typography component="li" variant="body1" sx={{ mb: 1 }}>
                    {t('legalDisclaimer.sections.liability.thirdPartyActions')}
                  </Typography>
                </Box>
                <Typography variant="body1" sx={{ mb: 2, fontWeight: 'bold' }}>
                  {t('legalDisclaimer.sections.liability.maxLiability')}
                </Typography>
              </Box>

              <Divider sx={{ my: 4 }} />

              {/* Service Availability */}
              <Box sx={{ mb: 4 }}>
                <Typography 
                  variant="h5" 
                  component="h2" 
                  gutterBottom
                  sx={{ 
                    fontWeight: 600,
                    color: 'primary.main',
                    mb: 2 
                  }}
                >
                  {t('legalDisclaimer.sections.serviceAvailability.title')}
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  {t('legalDisclaimer.sections.serviceAvailability.content')}
                </Typography>
                <Box component="ul" sx={{ pl: 2, mb: 2 }}>
                  <Typography component="li" variant="body1" sx={{ mb: 1 }}>
                    {t('legalDisclaimer.sections.serviceAvailability.maintenance')}
                  </Typography>
                  <Typography component="li" variant="body1" sx={{ mb: 1 }}>
                    {t('legalDisclaimer.sections.serviceAvailability.technicalIssues')}
                  </Typography>
                  <Typography component="li" variant="body1" sx={{ mb: 1 }}>
                    {t('legalDisclaimer.sections.serviceAvailability.externalFactors')}
                  </Typography>
                  <Typography component="li" variant="body1" sx={{ mb: 1 }}>
                    {t('legalDisclaimer.sections.serviceAvailability.sourceAvailability')}
                  </Typography>
                </Box>
              </Box>

              <Divider sx={{ my: 4 }} />

              {/* Data Accuracy */}
              <Box sx={{ mb: 4 }}>
                <Typography 
                  variant="h5" 
                  component="h2" 
                  gutterBottom
                  sx={{ 
                    fontWeight: 600,
                    color: 'primary.main',
                    mb: 2 
                  }}
                >
                  {t('legalDisclaimer.sections.dataAccuracy.title')}
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  {t('legalDisclaimer.sections.dataAccuracy.content')}
                </Typography>
                <Box component="ul" sx={{ pl: 2, mb: 2 }}>
                  <Typography component="li" variant="body1" sx={{ mb: 1 }}>
                    {t('legalDisclaimer.sections.dataAccuracy.sourceErrors')}
                  </Typography>
                  <Typography component="li" variant="body1" sx={{ mb: 1 }}>
                    {t('legalDisclaimer.sections.dataAccuracy.publicationDelays')}
                  </Typography>
                  <Typography component="li" variant="body1" sx={{ mb: 1 }}>
                    {t('legalDisclaimer.sections.dataAccuracy.formatChanges')}
                  </Typography>
                  <Typography component="li" variant="body1" sx={{ mb: 1 }}>
                    {t('legalDisclaimer.sections.dataAccuracy.accessRestrictions')}
                  </Typography>
                </Box>
              </Box>

              <Divider sx={{ my: 4 }} />

              {/* Intellectual Property */}
              <Box sx={{ mb: 4 }}>
                <Typography 
                  variant="h5" 
                  component="h2" 
                  gutterBottom
                  sx={{ 
                    fontWeight: 600,
                    color: 'primary.main',
                    mb: 2 
                  }}
                >
                  {t('legalDisclaimer.sections.intellectualProperty.title')}
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  {t('legalDisclaimer.sections.intellectualProperty.content')}
                </Typography>
                <Box component="ul" sx={{ pl: 2, mb: 2 }}>
                  <Typography component="li" variant="body1" sx={{ mb: 1 }}>
                    {t('legalDisclaimer.sections.intellectualProperty.reproduce')}
                  </Typography>
                  <Typography component="li" variant="body1" sx={{ mb: 1 }}>
                    {t('legalDisclaimer.sections.intellectualProperty.reverseEngineer')}
                  </Typography>
                  <Typography component="li" variant="body1" sx={{ mb: 1 }}>
                    {t('legalDisclaimer.sections.intellectualProperty.commercialUse')}
                  </Typography>
                  <Typography component="li" variant="body1" sx={{ mb: 1 }}>
                    {t('legalDisclaimer.sections.intellectualProperty.dataResale')}
                  </Typography>
                </Box>
              </Box>

              <Divider sx={{ my: 4 }} />

              {/* Service Termination */}
              <Box sx={{ mb: 4 }}>
                <Typography 
                  variant="h5" 
                  component="h2" 
                  gutterBottom
                  sx={{ 
                    fontWeight: 600,
                    color: 'primary.main',
                    mb: 2 
                  }}
                >
                  {t('legalDisclaimer.sections.termination.title')}
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  {t('legalDisclaimer.sections.termination.content')}
                </Typography>
                <Box component="ul" sx={{ pl: 2, mb: 2 }}>
                  <Typography component="li" variant="body1" sx={{ mb: 1 }}>
                    {t('legalDisclaimer.sections.termination.violations')}
                  </Typography>
                  <Typography component="li" variant="body1" sx={{ mb: 1 }}>
                    {t('legalDisclaimer.sections.termination.technicalReasons')}
                  </Typography>
                  <Typography component="li" variant="body1" sx={{ mb: 1 }}>
                    {t('legalDisclaimer.sections.termination.legalRequirements')}
                  </Typography>
                  <Typography component="li" variant="body1" sx={{ mb: 1 }}>
                    {t('legalDisclaimer.sections.termination.businessDecisions')}
                  </Typography>
                </Box>
              </Box>

              <Divider sx={{ my: 4 }} />

              {/* Governing Law */}
              <Box sx={{ mb: 4 }}>
                <Typography 
                  variant="h5" 
                  component="h2" 
                  gutterBottom
                  sx={{ 
                    fontWeight: 600,
                    color: 'primary.main',
                    mb: 2 
                  }}
                >
                  {t('legalDisclaimer.sections.governingLaw.title')}
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  {t('legalDisclaimer.sections.governingLaw.content')}
                </Typography>
              </Box>

              <Divider sx={{ my: 4 }} />

              {/* Disclaimer Changes */}
              <Box sx={{ mb: 4 }}>
                <Typography 
                  variant="h5" 
                  component="h2" 
                  gutterBottom
                  sx={{ 
                    fontWeight: 600,
                    color: 'primary.main',
                    mb: 2 
                  }}
                >
                  {t('legalDisclaimer.sections.disclaimerChanges.title')}
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  {t('legalDisclaimer.sections.disclaimerChanges.content')}
                </Typography>
              </Box>

              <Divider sx={{ my: 4 }} />

              {/* Contact Information */}
              <Box sx={{ mb: 2 }}>
                <Typography 
                  variant="h5" 
                  component="h2" 
                  gutterBottom
                  sx={{ 
                    fontWeight: 600,
                    color: 'primary.main',
                    mb: 2 
                  }}
                >
                  {t('legalDisclaimer.sections.contact.title')}
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  {t('legalDisclaimer.sections.contact.content')}
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  {t('legalDisclaimer.sections.contact.email')}
                </Typography>
                <Typography variant="body1" sx={{ mb: 2, fontStyle: 'italic' }}>
                  {t('legalDisclaimer.sections.contact.disclaimer')}
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Box>
      </Container>
    </>
  );
};

export default LegalDisclaimer;